import { get } from 'lodash';

import { formatValue, getAddedOrDeletedMessage, getValClassName, renderEdition, renderValue } from './common';

import type { ChangelogRule, FormatterContext } from './types';

export const PRICING_MODEL_ORDER = 1300;

const BID_MODEL_FIELDS = ['bid.dynamicBidPrice', 'bid.price', 'bid.pricingModel', 'distribution', 'mediaType'] as const;

type BidModelField = (typeof BID_MODEL_FIELDS)[number];

function getBidModelFieldLabel(field: string, { formatMessage }: FormatterContext): string {
  const labels: Record<BidModelField, string> = {
    'bid.dynamicBidPrice': formatMessage({
      defaultMessage: 'dynamic bid price',
      id: 'changelogModal.message.dynamicBidPrice'
    }),
    'bid.price': formatMessage({ defaultMessage: 'price', id: 'changelogModal.message.price' }),
    'bid.pricingModel': formatMessage({ defaultMessage: 'pricing model', id: 'changelogModal.message.pricingModel' }),
    distribution: formatMessage({ defaultMessage: 'distribution', id: 'changelogModal.message.distribution' }),
    mediaType: formatMessage({ defaultMessage: 'media type', id: 'changelogModal.message.mediaType' })
  };
  return labels[field as BidModelField] ?? field;
}

/** "Bid model 2": bid models are an array, identified by 1-based position */
function getBidModelLabel(position: unknown, { formatMessage }: FormatterContext): string {
  return `${formatMessage({ defaultMessage: 'Bid model', id: 'changelogModal.message.bidModel' })} ${
    Number(position) + 1
  }`;
}

/** `openRtb f-cpm` */
function describeBidModel(bidModel: unknown): string {
  return [get(bidModel, 'mediaType'), get(bidModel, 'bid.pricingModel')].map(formatValue).join(' ');
}

export const pricingModelRules: ChangelogRule[] = [
  {
    format: (change, context) => {
      const [, , position, ...fieldPath] = change.path ?? [];
      return renderEdition(
        `${getBidModelLabel(position, context)} ${getBidModelFieldLabel(fieldPath.join('.'), context)}`,
        formatValue(change.lhs),
        formatValue(change.rhs),
        context
      );
    },
    order: PRICING_MODEL_ORDER,
    regex:
      /^[END]\.pricingModelConfig\.bidModels\.\d+\.(bid\.dynamicBidPrice|bid\.price|bid\.pricingModel|distribution|mediaType)$/,
    section: 'pricingModel'
  },
  {
    format: (change, { formatMessage }) => {
      const added = change.item?.kind === 'N';
      return (
        <div className={getValClassName(added ? 'add' : 'delete')}>
          <b>{formatMessage({ defaultMessage: 'Bid model', id: 'changelogModal.message.bidModel' })}</b>
          {` ${getAddedOrDeletedMessage(added, formatMessage)}: `}
          <b>{describeBidModel(added ? change.item?.rhs : change.item?.lhs)}</b>
        </div>
      );
    },
    order: PRICING_MODEL_ORDER,
    regex: /^A\.pricingModelConfig\.bidModels$/,
    section: 'pricingModel'
  },
  {
    format: (change, context) =>
      renderEdition(
        `${context.formatMessage({ defaultMessage: 'Pricing model', id: 'changelogModal.message.pricingModelConfig' })} ${String(
          change.path?.[1]
        )}`,
        renderValue(change.lhs, context),
        renderValue(change.rhs, context),
        context
      ),
    order: PRICING_MODEL_ORDER + 1,
    regex: /^[END]\.pricingModelConfig\.[^.]+$/,
    section: 'pricingModel'
  }
];
